import {Inject, Injectable} from '@angular/core';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { SnackBarLoaderComponent } from '@flamingo/component/snackbar-loader.component';
import {
  FlamingoAlertDialogComponent,
  FlamingoAlertDialogModel,
} from '@flamingo/component/flamingo-alert-dialog.component';
import {Observable, of} from 'rxjs';
import {
  FlamingoConfirmDialogComponent,
  FlamingoConfirmDialogOptions,
} from '../component/flamingo-confirm-dialog.component';
import {FlamingoInputDialogComponent, FlamingoInputDialogOptions} from '../component/flamingo-input-dialog.component';
import {
  FlamingoOptConfirmDialogComponent,
  FlamingoOptConfirmDialogOptions,
  FlamingoOptConfirmDialogResult
} from '@flamingo/component/flamingo-opt-confirm-dialog.component';
import {tap} from 'rxjs/operators';
import {FLAMINGO_APP_CONFIG, FlamingoAppConfig} from '@flamingo/flamingo.config';

@Injectable({
  providedIn: 'root',
})
export class FlamingoMessageService {
  private storagePrefix: string;

  constructor(
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    @Inject(FLAMINGO_APP_CONFIG) config?: FlamingoAppConfig,
  ) {
    this.storagePrefix = config?.storagePrefix ?? 'flamingo_';
  }

  showMessage(message: string, duration: number = 5000): void {
    this.snackBar.open(message, undefined, { duration });
  }

  showMessageWithLoader(message: string): void {
    const config = new MatSnackBarConfig();
    config.data = {
      message,
    };
    this.snackBar.openFromComponent(SnackBarLoaderComponent, config);
  }

  showMessageWithConfirm(message: string, action: string = 'OK'): void {
    this.snackBar.open(message, action);
  }

  showAlert(data: FlamingoAlertDialogModel, config?: MatDialogConfig): Observable<void> {
    return this.dialog.open(FlamingoAlertDialogComponent, { ...config, data }).afterClosed();
  }

  showModal(component: any, data: any = {}, autoFocus?: boolean, disableClose?: boolean, width?: number): Observable<any> {
    return this.dialog.open(component, {
      width: (width ? width : '600') + 'px',
      maxHeight: '95vh',
      maxWidth: '90vw',
      autoFocus,
      disableClose,
      data,
    }).afterClosed();
  }

  showConfirmDialog(options: FlamingoConfirmDialogOptions, config?: MatDialogConfig): Observable<boolean> {
    return this.dialog.open(FlamingoConfirmDialogComponent, {
      ...config,
      data: options,
    }).afterClosed();
  }

  showOptConfirmDialog(options: FlamingoOptConfirmDialogOptions, config?: MatDialogConfig): Observable<FlamingoOptConfirmDialogResult> {
    if (localStorage.getItem(this.storagePrefix + options.key) === 'true') {
      return of({result: true, neverAskAgain: true})
    } else {
      return this.dialog.open(FlamingoOptConfirmDialogComponent, {
        ...config,
        data: options,
      }).afterClosed().pipe(
        tap(res => {
          if (res.result) {
            localStorage.setItem(this.storagePrefix + options.key, res.neverAskAgain.toString());
          }
        })
      );
    }
  }

  showInputDialog(options: FlamingoInputDialogOptions, config?: MatDialogConfig): Observable<string> {
    return this.dialog.open(FlamingoInputDialogComponent, {
      ...config,
      data: options,
    }).afterClosed();
  }

}
